import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

export type McpSetupStatus = 'copied' | 'no_compatible_ai' | null;

export interface OnboardingStatus {
  step: 0 | 1 | 2;
  completed_at: string | null;
  started_at: string | null;
  mcp_setup_status: McpSetupStatus;
  mobile_seen_at: string | null;
  mobile_email_sent_at: string | null;
  page1_saved: boolean;
  page2_saved: boolean;
}

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Fetch onboarding state. Returns {status, loading, error, refetch}.
 *
 * `enabled` controls whether the fetch fires at all — used by ProtectedRoute
 * to skip the call until the user has actually signed in.
 */
export function useOnboardingStatus(enabled: boolean = true) {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const aliveRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    if (!enabled) return;
    try {
      const token = await getToken();
      if (!token) {
        if (aliveRef.current) {
          setStatus(null);
          setLoading(false);
        }
        return;
      }
      const res = await fetch('/api/onboarding/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 410) {
        // Profile deleted — let WebpagesContext handle sign-out elsewhere.
        if (aliveRef.current) {
          setStatus(null);
          setLoading(false);
        }
        return;
      }
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = (await res.json()) as OnboardingStatus;
      if (aliveRef.current) {
        setStatus(data);
        setError(null);
      }
    } catch (err) {
      if (aliveRef.current) setError(err as Error);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    aliveRef.current = true;
    fetchStatus();
    return () => {
      aliveRef.current = false;
    };
  }, [fetchStatus]);

  return { status, loading, error, refetch: fetchStatus };
}
